import {Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild} from '@angular/core';
import {FormGroup, ReactiveFormsModule} from "@angular/forms";
import {IconBadgeComponent} from "../icon-badge/icon-badge.component";
import {DropDownListComponent, DropDownListModule, SortOrder} from "@syncfusion/ej2-angular-dropdowns";
import {FilteringEventArgs, PopupEventArgs} from "@syncfusion/ej2-dropdowns";

import {TYPE} from "../../types/types";
import {ChangeEventArgs} from '@syncfusion/ej2-angular-buttons';
import {AvatarComponent} from "../avatar/avatar.component";

@Component({
    selector: 'app-form-dropdown',
    imports: [
        IconBadgeComponent,
        DropDownListModule,
        ReactiveFormsModule,
        AvatarComponent
    ],
    templateUrl: './form-dropdown.component.html',
    styleUrl: './form-dropdown.component.scss'
})
export class FormDropdownComponent implements OnInit, OnChanges {
  @ViewChild('dropdownRef', {static: false}) public dropdownRef!: DropDownListComponent;
  
  @Input() public fCN: string = '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() public dataSource: any[] = [];
  @Input() public mapping: TYPE.ExtendedFieldSettingsModel = {text: 'name', value: 'id'};
  @Input() public disabled: boolean = false;
  @Input() public cssClass: string = '';
  @Input() public fG: FormGroup = new FormGroup({});
  @Input() public placeholder: string = '';
  @Input() public icon: string = '';
  @Input() public imageField: string = '';
  @Input() public sortOrder: SortOrder = 'None';
  @Input() public marker: 'priority' | null = null;
  @Input() public withEmpty: boolean = false;
  @Input() public allowFiltering: boolean = false;
  @Input() public returnObject: boolean = false;
  @Input() public popupWidth: string = '100%';
  @Input() public width: string = '';
  @Input() public errorText: string = '';
  @Input() public hasErrorField: boolean = false;
  @Output() public  valueChange: EventEmitter<string | number | TYPE.FieldSettingsModelWithId> = new EventEmitter<string | number | TYPE.FieldSettingsModelWithId>();
  @Output() public  reachedEndOfList: EventEmitter<void> = new EventEmitter<void>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() public  search: EventEmitter<string> = new EventEmitter<string>();

  private popupScrollPosition: number = 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private popupElement: any = null;
  private pendingFilterEvent: FilteringEventArgs | null = null;


  public ngOnInit(): void {
    // Add empty option if needed
    if (this.withEmpty) {
      this.dataSource.unshift({id: '', name: this.placeholder});
    }

    // Disable the dropdown if needed
    if (this.disabled) {
      this.fG.controls[this.fCN].disable();
    }
  }

  public ngOnChanges(): void {
    // Restore scroll position if needed
    if (this.popupElement && this.popupScrollPosition > 0) {
      setTimeout(() => {
        this.popupElement.scrollTop = this.popupScrollPosition
      }, 5);
    }
    
    // Refresh dropdown when dataSource changes
    if (this.dropdownRef && this.dataSource) {
      setTimeout(() => {
        this.dropdownRef.dataSource = this.dataSource;
        this.dropdownRef.dataBind();
      }, 0);
    }
    
    // If we have a pending filter event, update it with the new dataSource
    if (this.pendingFilterEvent) {
      setTimeout(() => {
        this.pendingFilterEvent!.updateData(this.dataSource);
        this.pendingFilterEvent = null;
      }, 0);
    }
  }

  public onChange(event: ChangeEventArgs): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tmp: TYPE.FieldSettingsModelWithId = (event as any).itemData as TYPE.FieldSettingsModelWithId;
    if (tmp && tmp.id) {
      if (this.returnObject) {
        this.valueChange.emit(tmp);
      } else {
        this.valueChange.emit(tmp.id);
      }
    }
  }

  public filter(event: FilteringEventArgs): void {
    // Prevent default filtering, we'll handle it ourselves
    event.preventDefaultAction = true;
    
    // Store the event so we can update it when dataSource changes
    this.pendingFilterEvent = event;
    
    // Emit search event to parent - parent will update dataSource, triggering ngOnChanges
    this.search.emit(event.text);
  }

  public checkEndOfList(event: PopupEventArgs): void {
    const popupElement = event.popup.element.querySelector('div.e-content.e-dropdownbase');
    this.popupElement = popupElement;
    if (popupElement) {
      popupElement.addEventListener('scroll', () => {
        const {scrollTop, scrollHeight, clientHeight} = popupElement;
        if (scrollTop + clientHeight >= scrollHeight) {
          this.popupScrollPosition = scrollHeight - clientHeight;
          this.reachedEndOfList.emit();
        }
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getItemVisual(data: any | null): { kind: 'image' | 'icon'; value: string } | null {
    if (!data) return null;
    const m = this.mapping as TYPE.ExtendedFieldSettingsModel;

    // Prefer image when both exist
    if (m.image && typeof data[m.image] === 'string' && (data[m.image] as string).trim()) {
      return {kind: 'image', value: data[m.image] as string};
    }
    if (m.icon && typeof data[m.icon] === 'string' && (data[m.icon] as string).trim()) {
      return {kind: 'icon', value: data[m.icon] as string};
    }
    return null;
  }
}
